import {
  Component,
  OnInit,
  inject
} from '@angular/core';
import { CommonModule, DatePipe, formatDate } from '@angular/common';
import * as XLSX from 'xlsx';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Order } from '../../_interfaces/order';
import { FormsModule } from '@angular/forms';
import { OrderService } from '../../_services/order.service';
import { AlertService } from '../../_services/alert.service';
import { Router } from '@angular/router';
import { UserService } from '../../_services/users.service';
import { orderStatus, userRoles } from '../../../Shared/_constants/constants';
import { MatDialog } from '@angular/material/dialog';
import { ViewOrderModalComponent } from './view-order-modal/view-order-modal.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { formatDeliveryTerms } from '../delivery/dispatch-date';
import { LostSalesComponent, LostSalesTotals } from './lost-sales/lost-sales.component';
import { AbandonedCartsComponent, AbandonedCartTotals } from './abandoned-carts/abandoned-carts.component';
import { LeadPanel, SalesIdeasComponent } from './sales-ideas/sales-ideas.component';
import { OrderActivityComponent } from './order-activity/order-activity.component';
import { PaginatorPagesDirective } from '../../../Shared/_directives/paginator-pages.directive';
@Component({
    selector: 'app-sales',
    imports: [PaginatorPagesDirective,
        MainLayoutComponent,
        MatButtonModule,
        MatPaginatorModule,
        DatePipe,
        FormsModule,
        MatTooltipModule,
        MatMenuModule,
        MatIconModule,
        CommonModule,
        LostSalesComponent,
        AbandonedCartsComponent,
        SalesIdeasComponent,
        OrderActivityComponent
    ],
    templateUrl: './sales.component.html',
    styleUrl: './sales.component.css'
})
export class SalesComponent implements OnInit {
  // Sorting works on the page that's showing (each page is fetched from the
  // server), same as the mat-sort headers it replaces.
  readonly sortableColumns: { key: string; label: string; cls: string }[] = [
    { key: 'orderNo', label: 'Order', cls: 'px-5' },
    { key: 'customer', label: 'Customer', cls: 'px-4' },
    { key: 'orderStatus', label: 'Status', cls: 'px-4' },
    { key: 'paidDate', label: 'Payment', cls: 'px-4' },
    { key: 'deliveryMethod', label: 'Delivery', cls: 'px-4' },
    { key: 'netAmount', label: 'Amount', cls: 'px-4 text-right' },
    { key: 'agent', label: 'Agent', cls: 'px-4' },
  ];
  sortKey = '';
  sortDir: 'asc' | 'desc' = 'asc';
  rows: any[] = [];
  orders: Order[] = [];
  agents: any = null;
  agent: number = 0;
  products: any;
  fromDate = formatDate(new Date(), "yyyy-MM-dd", "en");
  toDate =formatDate(new Date(), "yyyy-MM-dd", "en");
  productName : string = '';
  postCode : string = '';
  status: string = orderStatus.PENDING;
  paymentMethod: string = 'ALL'
  customer: string = '';
  discountCoupon: string = '';
  origin: string = '';
  phoneNo: string = '';
  email: string = '';
  // Shows the traffic source without its "_cpc" tag or a ".com" ending,
  // e.g. google_cpc -> google, chatgpt.com_cpc -> chatgpt. Only for display:
  // the filter still works on the stored value.
  originLabel(origin: string | null | undefined): string {
    if (!origin) return '';
    const i = origin.indexOf('_cpc');
    const base = i > 0 ? origin.substring(0, i) : origin;
    return base.replace(/\.com$/i, '');
  }

  originOptions: string[] = ['','google_cpc','facebook_cpc','newsletter_cpc','newsletter','chatgpt.com_cpc','google','bing_cpc','fb_cpc','attentive_cpc','ig_cpc','trustpilot_cpc','klarna_cpc','chatgpt_ads_cpc'];
  pageNumber  = 0;
  itemsPerPage = 50;
  count = 0;
  searchOrderNo = '';
  exporting = false;
  private lastParams: any = null;

  // ---- Search & filter UI ----
  quickSearch = '';
  showMoreFilters = false;

  readonly mainStatuses = ['ALL', 'PENDING', 'PROCESSING', 'COMPLETED', 'DELAYED COMPLETION', 'ON HOLD', 'CANCELLED', 'VOID'];
  readonly otherStatuses = ['PENDING COLLECTION', 'REFUNDED', 'PARTIALLY REFUNDED', 'CHARGE BACK', 'FAILED'];

  readonly paymentMethods = [
    { value: 'ALL', label: 'All methods' },
    { value: 'TEYA', label: 'Teya' },
    { value: 'TRUST_PAYMENT', label: 'Trust Payment' },
    { value: 'PAYPAL', label: 'PayPal' },
    { value: 'PAYLATR', label: 'Pay Later' },
    { value: 'INVOICE', label: 'Invoice' },
    { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
    { value: 'NO-PAYMENT', label: 'No Payment' },
  ];

  readonly datePresets = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: '7d', label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: 'month', label: 'This month' },
    { key: 'custom', label: 'Custom' },
  ];
  datePreset = '30d';

  // Text filters that live under "More filters".
  readonly moreFields: { key: 'productName' | 'customer' | 'postCode' | 'phoneNo' | 'email' | 'discountCoupon'; label: string; placeholder: string }[] = [
    { key: 'productName', label: 'Product', placeholder: 'Name or SKU - separate several with commas' },
    { key: 'customer', label: 'Customer', placeholder: 'Name - separate several with commas' },
    { key: 'postCode', label: 'Postcode', placeholder: 'e.g. CO6 1EP, CO1 2AB' },
    { key: 'phoneNo', label: 'Phone', placeholder: 'Number - separate several with commas' },
    { key: 'email', label: 'Email', placeholder: 'Email - separate several with commas' },
    { key: 'discountCoupon', label: 'Discount coupon', placeholder: 'Code - separate several with commas' },
  ];
  searchParams: { [key: string]: any } = {};
  userInfo: any;
  readonly dialog = inject(MatDialog);

  constructor(
    private orderService: OrderService,
    private userService: UserService,
    private alertService: AlertService,
    private router: Router,) {}

  ngOnInit(): void {
    this.userInfo = this.userService.isLoggedIn();
    this.loadLostTotals();
    this.loadSampleTotals();
    this.loadCartTotals();
    // enable following for dev testing
    this.status = 'ALL';
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 30);
    const fromDate = formatDate(sevenDaysAgo, 'yyyy-MM-dd', 'en');
    this.fromDate = fromDate;
    this.orderService.getSearchParams().subscribe((params) => (this.searchParams = params));
    if(this.searchParams && Object.keys(this.searchParams).length > 0){
      this.fromDate = this.searchParams['dtFrom'] ? this.searchParams['dtFrom'] : this.fromDate;
      this.toDate = this.searchParams['dtTo'] ? this.searchParams['dtTo'] : this.toDate;
      this.productName = this.searchParams['product'] ? this.searchParams['product'] : '';
      this.customer = this.searchParams['customer'] ? this.searchParams['customer'] : '';
      this.agent = this.searchParams['agentId'] ? this.searchParams['agentId'] : 0;
      this.postCode = this.searchParams['postCode'] ? this.searchParams['postCode'] : '';
      this.status = this.searchParams['status'] ? this.searchParams['status'] : this.status;
      this.searchOrderNo = this.searchParams['orderNo'] ? this.searchParams['orderNo'] : '';
      this.paymentMethod = this.searchParams['paymentMethod'] ? this.searchParams['paymentMethod'] : 'ALL';
      this.discountCoupon = this.searchParams['discountCoupon'] ? this.searchParams['discountCoupon'] : '';
      this.origin = this.searchParams['origin'] ?? '';
      this.phoneNo = this.searchParams['phoneNo'] ?? '';
      this.email = this.searchParams['email'] ?? '';
    }
    this.datePreset = this.detectPreset();
    this.showMoreFilters = this.moreFilterCount > 0;
    this.loadAgents();
    this.loadOrders(0, this.itemsPerPage);
  }

  // ---- Lost sales: customers who didn't go through with an order ----
  lostOpen = false;
  lostTotals: LostSalesTotals | null = null;
  // Customers who took free samples but haven't ordered.
  samplesOpen = false;
  sampleTotals: LostSalesTotals | null = null;

  private loadSampleTotals(): void {
    this.orderService.getSampleLeads({ days: 30, countOnly: true }).subscribe({
      next: (r: any) => (this.sampleTotals = { count: r.count, notContacted: r.notContacted, firstTime: r.firstTime, readyToCall: r.readyToCall }),
      error: () => (this.sampleTotals = null),
    });
  }

  // Today's orders with one status, opened from "Today so far".
  statusOrdersOpen: string | null = null;

  // "Ideas for this hour" asks for one of the follow-up lists.
  openLeadPanel(panel: LeadPanel): void {
    this.lostOpen = panel === 'lost';
    this.samplesOpen = panel === 'samples';
    this.cartsOpen = panel === 'carts';
  }

  // Website checkouts that left an email or phone but no order.
  cartsOpen = false;
  cartTotals: AbandonedCartTotals | null = null;

  private loadCartTotals(): void {
    this.orderService.getAbandonedCarts({ days: 30, countOnly: true }).subscribe({
      next: (r: any) => (this.cartTotals = { count: r.count, value: r.value, notContacted: r.notContacted, returning: r.returning }),
      // Hidden for roles without access, and before the migration has run.
      error: () => (this.cartTotals = null),
    });
  }

  private loadLostTotals(): void {
    this.orderService.getLostSales({ days: 90, countOnly: true }).subscribe({
      next: (r: any) => (this.lostTotals = { count: r.count, pending: r.pending, cancelled: r.cancelled, value: r.value, notContacted: r.notContacted }),
      // A missing badge is fine - the rest of the page still works.
      error: () => (this.lostTotals = null),
    });
  }

  onCreateSale() {
    this.router.navigate(['sales/create'])
  }


  private async loadAgents() {
    await this.userService.getCustomers('SALES,SALEADMIN').subscribe({
      next: (response: any) => {
        this.agents = response;
      },
      error: (error: { error: { message: any; }; }) => this.alertService.alert(error.error.message, 'error'),
    });
  }

  onResetFilters() {
    this.orderService.setSearchParams({});
    this.status = 'ALL';
    this.applyDatePreset('30d', false);
    this.productName = '';
    this.customer = '';
    this.discountCoupon = '';
    this.paymentMethod = 'ALL';
    this.origin = '';
    this.phoneNo = '';
    this.email = '';
    this.agent = 0;
    this.postCode = '';
    this.searchOrderNo = '';
    this.quickSearch = '';
    this.pageNumber = 0;
    this.loadOrders(0, this.itemsPerPage);
  }

  // ---- Smart search: works out what was typed and fills the right filter ----

  // Several values can go in one go, separated by commas: "99215901, 99215902"
  // or "CO6 1EP, mary@example.com". Each one is sorted into the right filter
  // and added to whatever is already there.
  private static splitTerms(text: string): string[] {
    return String(text ?? '')
      .split(/[,;\n]+/)
      .map((t) => t.trim())
      .filter(Boolean);
  }

  private classify(q: string): { field: string; label: string; icon: string } {
    if (q.includes('@')) return { field: 'email', label: 'Email', icon: 'mail' };
    if (/^[+0][\d\s()-]{6,}$/.test(q)) return { field: 'phoneNo', label: 'Phone', icon: 'call' };
    if (/^\d+$/.test(q)) return { field: 'orderNo', label: 'Order no.', icon: 'receipt_long' };
    if (/^[A-Za-z]{1,2}\d[A-Za-z\d]?(\s?\d[A-Za-z]{2})?$/.test(q)) return { field: 'postCode', label: 'Postcode', icon: 'place' };
    return { field: 'customer', label: 'Customer', icon: 'person' };
  }

  // The tag shown inside the search box: what the text will be searched as.
  get quickSearchKind(): { label: string; icon: string } | null {
    const terms = SalesComponent.splitTerms(this.quickSearch);
    if (!terms.length) return null;
    const kinds = new Map(terms.map((t) => [this.classify(t).field, this.classify(t)]));
    const first = [...kinds.values()][0];
    if (kinds.size > 1) return { label: `${terms.length} terms`, icon: 'manage_search' };
    return { label: terms.length > 1 ? `${first.label} x${terms.length}` : first.label, icon: first.icon };
  }

  private valuesOf(field: string): string[] {
    return SalesComponent.splitTerms(this.getField(field));
  }

  private addValues(field: string, values: string[]) {
    const merged = this.valuesOf(field);
    for (const v of values) {
      if (!merged.some((m) => m.toLowerCase() === v.toLowerCase())) merged.push(v);
    }
    this.setField(field, merged.join(', '));
  }

  private removeValue(field: string, value: string) {
    this.setField(field, this.valuesOf(field).filter((v) => v !== value).join(', '));
  }

  onQuickSearch() {
    const grouped = new Map<string, string[]>();
    for (const term of SalesComponent.splitTerms(this.quickSearch)) {
      const field = this.classify(term).field;
      grouped.set(field, [...(grouped.get(field) ?? []), term]);
    }
    if (grouped.size) {
      // Searching by something other than an order number leaves "order
      // number" mode; order numbers alongside other terms keep it.
      if (!grouped.has('orderNo')) this.searchOrderNo = '';
      for (const [field, values] of grouped) {
        this.addValues(field, values);
      }
    }
    this.quickSearch = '';
    this.applyFilters();
  }

  clearOrderNo() {
    this.searchOrderNo = '';
    this.applyFilters();
  }

  // ---- Filters ----

  applyFilters() {
    this.pageNumber = 0;
    this.loadOrders(0, this.itemsPerPage);
  }

  setStatus(status: string) {
    this.status = status;
    this.applyFilters();
  }

  get isOtherStatus(): boolean {
    return this.otherStatuses.includes(this.status);
  }

  getField(key: string): string {
    return key === 'orderNo' ? this.searchOrderNo : (this as any)[key] ?? '';
  }

  setField(key: string, value: string) {
    if (key === 'orderNo') this.searchOrderNo = value ?? '';
    else (this as any)[key] = value ?? '';
  }

  get orderNoCount(): number {
    return this.valuesOf('orderNo').length;
  }

  get moreFilterCount(): number {
    return this.moreFields.filter((f) => !!this.getField(f.key)).length;
  }

  // ---- Order date range ----

  private presetRange(key: string): { from: string; to: string } | null {
    const fmt = (d: Date) => formatDate(d, 'yyyy-MM-dd', 'en');
    const today = new Date();
    const shift = (days: number) => {
      const d = new Date(today);
      d.setDate(d.getDate() - days);
      return d;
    };
    switch (key) {
      case 'today': return { from: fmt(today), to: fmt(today) };
      case 'yesterday': return { from: fmt(shift(1)), to: fmt(shift(1)) };
      case '7d': return { from: fmt(shift(6)), to: fmt(today) };
      case '30d': return { from: fmt(shift(30)), to: fmt(today) };
      case 'month': return { from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), to: fmt(today) };
      default: return null;
    }
  }

  private detectPreset(): string {
    const match = this.datePresets.find((p) => {
      const r = this.presetRange(p.key);
      return r && r.from === this.fromDate && r.to === this.toDate;
    });
    return match?.key ?? 'custom';
  }

  applyDatePreset(key: string, reload = true) {
    this.datePreset = key;
    const range = this.presetRange(key);
    if (range) {
      this.fromDate = range.from;
      this.toDate = range.to;
    }
    if (reload && key !== 'custom') this.applyFilters();
  }

  onCustomDateChange() {
    this.datePreset = this.detectPreset();
    if (this.fromDate && this.toDate) this.applyFilters();
  }

  // ---- Active filter chips ----

  get activeChips(): { key: string; field: string; label: string; value: string }[] {
    const chips: { key: string; field: string; label: string; value: string }[] = [];
    const multi = (field: string, label: string) => {
      for (const v of this.valuesOf(field)) chips.push({ key: `${field}:${v}`, field, label, value: v });
    };
    multi('orderNo', 'Order');
    // An order number search ignores every other filter.
    if (this.searchOrderNo) return chips;

    if (this.status && this.status !== 'ALL') chips.push({ key: 'status', field: 'status', label: 'Status', value: this.status.toLowerCase() });
    if (this.datePreset !== '30d') {
      const preset = this.datePresets.find((p) => p.key === this.datePreset);
      chips.push({
        key: 'date',
        field: 'date',
        label: 'Date',
        value: this.datePreset === 'custom' ? `${this.fromDate || '…'} to ${this.toDate || '…'}` : preset!.label.toLowerCase(),
      });
    }
    if (this.paymentMethod && this.paymentMethod !== 'ALL') {
      chips.push({ key: 'paymentMethod', field: 'paymentMethod', label: 'Payment', value: this.paymentMethods.find((m) => m.value === this.paymentMethod)?.label ?? this.paymentMethod });
    }
    if (this.agent && Number(this.agent) !== 0) {
      const ag = (this.agents ?? []).find((a: any) => a.userid === Number(this.agent));
      chips.push({ key: 'agent', field: 'agent', label: 'Agent', value: ag?.firstname ?? String(this.agent) });
    }
    if (this.origin) chips.push({ key: 'origin', field: 'origin', label: 'Origin', value: this.originLabel(this.origin) });
    for (const f of this.moreFields) multi(f.key, f.label);
    return chips;
  }

  get hasActiveFilters(): boolean {
    return this.activeChips.length > 0;
  }

  removeChip(chip: { field: string; value: string }) {
    switch (chip.field) {
      case 'status': this.status = 'ALL'; break;
      case 'date': this.applyDatePreset('30d', false); break;
      case 'paymentMethod': this.paymentMethod = 'ALL'; break;
      case 'agent': this.agent = 0; break;
      case 'origin': this.origin = ''; break;
      default: this.removeValue(chip.field, chip.value);
    }
    this.applyFilters();
  }

  // Click a header: ascending, then descending, then back to the fetched order.
  sortBy(key: string) {
    if (this.sortKey !== key) {
      this.sortKey = key;
      this.sortDir = 'asc';
    } else if (this.sortDir === 'asc') {
      this.sortDir = 'desc';
    } else {
      this.sortKey = '';
    }
    this.applySort();
  }

  private sortValue(o: any, key: string): string | number {
    switch (key) {
      case 'orderNo': return Number(o.orderNo) || 0;
      case 'customer': return this.getCustomerName(o).toLowerCase();
      case 'orderStatus': return String(o.orderStatus ?? '');
      case 'paidDate': return o.paymentDate ? new Date(o.paymentDate).getTime() : 0;
      case 'deliveryMethod': return String(o.deliveryMethod ?? '');
      case 'netAmount': return Number(o.netAmount) || 0;
      case 'agent': return String(o.agent?.firstname ?? '').toLowerCase();
      default: return 0;
    }
  }

  private applySort() {
    if (!this.sortKey) {
      this.rows = [...this.orders];
      return;
    }
    const dir = this.sortDir === 'asc' ? 1 : -1;
    this.rows = [...this.orders].sort((a, b) => {
      const x = this.sortValue(a, this.sortKey);
      const y = this.sortValue(b, this.sortKey);
      return (x < y ? -1 : x > y ? 1 : 0) * dir;
    });
  }

  get rangeLabel(): string {
    if (!this.count) return '';
    const from = this.pageNumber * this.itemsPerPage + 1;
    const to = Math.min(from + this.orders.length - 1, this.count);
    return `Showing ${from}-${to} of ${this.count}`;
  }

  async loadOrders(pageIndex: number, pageSize: number) {
    let params: any = {
      dtFrom: this.fromDate,
      dtTo: this.toDate,
      product: this.productName,
      customer: this.customer,
      agentId: this.agent,
      postCode: this.postCode,
      status: this.status,
      discountCoupon: this.discountCoupon,
      paymentMethod: this.paymentMethod,
      origin: this.origin,
      phoneNo: this.phoneNo,
      email: this.email,
      pageIndex: pageIndex,
      pageSize: pageSize,
    };

    if (this.searchOrderNo) {
      params = {
        orderNo: this.searchOrderNo,
        pageIndex: pageIndex,
        pageSize: pageSize,
      }
    }
    this.orderService.setSearchParams(params);
    this.lastParams = params;
    await this.orderService.getOrders(params).subscribe({
      next: (response: any) => {
        this.orders = response.orders;
        this.count = response.count;
        this.applySort();
      },
      error: (error: { error: { message: any; }; }) => this.alertService.alert(error.error.message, 'error'),
    });
  }

  onView(rowData: any) {
    let modelConfig: any = {
      width: "50%",
      height: "700px",
      data: {
        type: 'VIEW',
        ...rowData
      },
    };
    const dialogRef = this.dialog.open(ViewOrderModalComponent,modelConfig);
  }

  onEdit(rowData: any) {
    this.orderService.setEditingOrderID(rowData.orderId, rowData.orderNo);
    this.orderService.setOrderNav({
      orders: this.rows.map((o: any) => ({ orderId: o.orderId, orderNo: o.orderNo })),
      params: this.lastParams,
      pageIndex: this.pageNumber,
      pageSize: this.itemsPerPage,
      count: this.count,
    });
    this.router.navigate(['sales', rowData.orderNo]);
  }

  public updateList(event: PageEvent): void {
    if (this.itemsPerPage !== event.pageSize) {
      this.itemsPerPage = event.pageSize || 10;
      this.pageNumber = 0;
    }
    else
      this.pageNumber = event.pageIndex;

    this.loadOrders(this.pageNumber, this.itemsPerPage );
  }

  syncOrder(orderId: number){
    this.orderService
    .syncOrder(orderId)
    .subscribe({
      next: (response) => {
        this.alertService.alert('Order sent to Odoo', 'success');
        this.loadOrders(this.pageNumber, this.itemsPerPage );
      },
      error: (error) => {
        this.alertService.alert(error.error.message, 'error');
      },
    });
  }

  // Google Maps search for the order's delivery address (falls back to the
  // billing one, and to just the postcode if that's all there is).
  mapsUrl(order: any): string {
    const addresses = order.OrderAddresses ?? [];
    const a = addresses.find((x: any) => x.addressType === 'SHIPPING') || addresses[0];
    const query = [a?.address1, a?.address2, a?.city, a?.postCode, a?.country].filter(Boolean).join(', ');
    return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(query);
  }

  postCodeOf(order: any): string {
    const addresses = order.OrderAddresses ?? [];
    const a = addresses.find((x: any) => x.addressType === 'SHIPPING') || addresses[0];
    return a?.postCode || '';
  }

  private static readonly DELIVERY_METHOD_LABELS: Record<string, string> = {
    STANDARD: 'Standard',
    EXPRESS: 'Express',
    NEXTDAY: 'Next Day',
    COLLECTION: 'Collection',
  };

  deliveryMethodLabel(order: any): string {
    return SalesComponent.DELIVERY_METHOD_LABELS[order.deliveryMethod] || order.deliveryMethod;
  }

  deliveryTermsShort(order: any): string | null {
    return formatDeliveryTerms(order.deliveryTerms);
  }

  // The account's name, or - for guest checkouts, which have none - the name
  // on the order's (billing) address, marked "(Guest)".
  getCustomerName(element: any): string {
    if (element.customer?.firstname && element.customer?.firstname.trim() !== '-') {
      return `${element.customer.firstname} ${element.customer.lastname ?? ''}`.trim();
    }
    const addresses: any[] = element.OrderAddresses ?? [];
    const address = addresses.find((a) => a?.addressType === 'BILLING' && (a.firstName || a.lastName)) ?? addresses.find((a) => a?.firstName || a?.lastName);
    const name = address ? `${address.firstName ?? ''} ${address.lastName ?? ''}`.replace(/\s+/g, ' ').trim() : '';
    const fallback = name || address?.email || element.customer?.useremail || '';
    return fallback ? `${fallback} (Guest)` : 'Guest';
  }

  exportToExcel() {
    const params: any = {
      dtFrom: this.fromDate,
      dtTo: this.toDate,
      product: this.productName,
      customer: this.customer,
      agentId: this.agent,
      postCode: this.postCode,
      status: this.status,
      discountCoupon: this.discountCoupon,
      paymentMethod: this.paymentMethod,
      origin: this.origin,
      phoneNo: this.phoneNo,
      email: this.email,
      orderNo: this.searchOrderNo || undefined,
      pageIndex: 0,
      pageSize: this.count || 10000,
    };
    this.exporting = true;
    this.orderService.getOrders(params).subscribe({
      next: (response: any) => {
        this.exporting = false;
        const allOrders = response.orders;
        const rows = allOrders.map((o: any, i: number) => ({
          'Sl No.': i + 1,
          'Order No': o.orderNo,
          'Order Date': o.orderDate ? new Date(o.orderDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '',
          'Paid Date': o.paymentDate ? new Date(o.paymentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '',
          'Customer': this.getCustomerName(o),
          'Order Status': o.orderStatus,
          'Payment Method': o.paymentMethod,
          'Order Total': o.netAmount,
          'Refund': o.refundTotal > 0 ? o.refundTotal : '',
          'Net Balance': (o.netAmount - o.refundTotal).toFixed(2),
          'Delivery Method': o.deliveryMethod,
          'Agent': o.agent && o.customerId !== o.userId ? o.agent.firstname : '',
          'Origin': this.originLabel(o.origin),
        }));
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
        XLSX.writeFile(workbook, 'orders.xlsx');
      },
      error: (error: { error: { message: any } }) => {
        this.exporting = false;
        this.alertService.alert(error.error.message, 'error');
      },
    });
  }

  getStatusClass(status: string): string {
    switch(status) {
      case 'PROCESSING': return 'bg-green-50 text-green-700';
      case 'COMPLETED': return 'bg-emerald-100 text-emerald-800';
      case 'DELAYED COMPLETION': return 'bg-lime-100 text-lime-800';
      case 'VOID':
      case 'CHARGE BACK': return 'bg-stone-200 text-stone-700';
      case 'CANCELLED':
      case 'FAILED': return 'bg-red-50 text-red-700';
      case 'REFUNDED':
      case 'PARTIALLY REFUNDED': return 'bg-orange-100 text-orange-800';
      case 'PENDING': return 'bg-yellow-50 text-yellow-700';
      case 'ON HOLD':
      case 'PENDING COLLECTION': return 'bg-orange-50 text-orange-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  }

}
